import { useIntl } from 'react-intl';
import { IconCircleCheck } from '@rossum/ui/icons/tabler';
import {
  alpha,
  Box,
  Button,
  Collapse,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  decideOfferedLookup,
  type OfferedLookup,
  useChatStore,
} from '../hooks/chatStore';

type LookupProposalProps = {
  messageId: string;
  offer: OfferedLookup;
  /** Only the conversation's last message can still be answered: a later one has moved past it. */
  canAnswer: boolean;
};

type Change = { index: number; text: string };

const ChangeList = ({ changes }: { changes: Change[] }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
    {changes.map(({ index, text }) => (
      <Typography key={index} component="li" variant="body2">
        {text}
      </Typography>
    ))}
  </Box>
);

/**
 * An offered lookup configuration under the agent's reply, and how the user answered it. Each
 * query's `"//"` comment is one change, read from the offer itself so it always matches what
 * "Create a draft" writes. The answer only shows here; the agent is not told.
 */
export const LookupProposal = ({
  messageId,
  offer,
  canAnswer,
}: LookupProposalProps) => {
  const intl = useIntl();
  const decision = useChatStore(s =>
    s.drawerChatId
      ? s.sessions[s.drawerChatId]?.lookupDecisions[messageId]
      : undefined
  );
  const canAccept = useChatStore(
    s => s.lookupTarget?.fieldSchemaId === offer.fieldSchemaId
  );

  // Keyed by position: two queries can carry the same explanation.
  const comments = offer.result.queries.flatMap((query, index) =>
    query['//'] ? [{ index, text: query['//'] }] : []
  );
  // `"//"` is optional: without any, the card still says what it would write.
  const changes =
    comments.length > 0
      ? comments
      : [
          {
            index: 0,
            text: intl.formatMessage(
              { id: 'features.chat.lookupProposal.summary' },
              { count: offer.result.queries.length }
            ),
          },
        ];

  return (
    <>
      <Collapse in={!decision} unmountOnExit>
        <Stack
          gap={1.5}
          data-cy="chat-lookup-proposal"
          sx={{
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: borderRadius('m'),
            color: canAnswer ? 'text.primary' : 'text.secondary',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {intl.formatMessage({ id: 'features.chat.lookupProposal.title' })}
          </Typography>
          <ChangeList changes={changes} />
          {canAnswer && (
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Button
                variant="contained"
                disabled={!canAccept}
                onClick={() => decideOfferedLookup(messageId, offer, 'draft')}
              >
                {intl.formatMessage({
                  id: 'features.chat.lookupProposal.createDraft',
                })}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => decideOfferedLookup(messageId, offer, 'kept')}
              >
                {intl.formatMessage({
                  id: 'features.chat.lookupProposal.keepCurrent',
                })}
              </Button>
              {!canAccept && (
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage(
                    { id: 'features.chat.lookupProposal.fieldNotOpen' },
                    { field: offer.fieldSchemaId }
                  )}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </Collapse>

      {decision === 'draft' && (
        <Stack gap={2} alignItems="flex-start">
          <Stack
            direction="row"
            gap={1}
            data-cy="chat-lookup-draft-created"
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: borderRadius('m'),
              bgcolor: theme => alpha(theme.palette.success.main, 0.16),
            }}
          >
            <SvgIcon sx={{ color: 'success.main' }}>
              <IconCircleCheck />
            </SvgIcon>
            <Stack gap={0.5}>
              <Typography variant="body2" fontWeight={600}>
                {intl.formatMessage({
                  id: 'features.chat.lookupProposal.draftCreated',
                })}
              </Typography>
              <ChangeList changes={changes} />
            </Stack>
          </Stack>
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'features.chat.lookupProposal.draftHint',
            })}
          </Typography>
        </Stack>
      )}
    </>
  );
};
